import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Input, Select, Spinner, Badge, RichTextEditor, FileAttachment } from '@components/ui';
import { TASK_PRIORITY, TASK_TYPE, STORY_POINTS, STATUS_CONFIG } from '@config/constants';
import TaskRelationSelector from '@components/features/TaskRelationSelector';
import CommentSection from '@components/features/CommentSection';
import ActivityFeed from '@components/features/ActivityFeed';
import { useTask, useUpdateTask, useDeleteTask, useTaskRelations, useTaskAttachments, useAddTaskAttachments, useDeleteTaskAttachment } from '@hooks';
import { useAuthStore, useUIStore } from '@store';
import { formatDay } from '@utils/calendarDate';

const priorityOptions = Object.entries(TASK_PRIORITY).map(([key, value]) => ({
  value: value,
  label: key.charAt(0) + key.slice(1).toLowerCase(),
}));

const typeOptions = Object.entries(TASK_TYPE).map(([key, value]) => ({
  value: value,
  label: key.charAt(0) + key.slice(1).toLowerCase(),
}));

const pointsOptions = STORY_POINTS.map((p) => ({
  value: p,
  label: `${p} puntos`,
}));

const priorityColors = {
  critical: { bg: '#fef2f2', color: '#dc2626', label: 'Crítica' },
  high: { bg: '#fff7ed', color: '#ea580c', label: 'Alta' },
  medium: { bg: '#fefce8', color: '#ca8a04', label: 'Media' },
  low: { bg: '#f0fdf4', color: '#16a34a', label: 'Baja' },
};

const typeIcons = {
  feature: '✨',
  bug: '🐛',
  improvement: '🔧',
  task: '📋',
};

export default function TaskDetailPage({ taskId, sprints = [], epics = [], users = [], onBack }) {
  const currentUser = useAuthStore((state) => state.user);
  const setActiveView = useUIStore((state) => state.setActiveView);
  const setSelectedTaskId = useUIStore((state) => state.setSelectedTaskId);
  const setSelectedSprintId = useUIStore((state) => state.setSelectedSprintId);

  const { data: taskData, isLoading } = useTask(taskId);
  const updateTask = useUpdateTask();
  const deleteTaskMutation = useDeleteTask();
  const { data: relationsData, refetch: refetchRelations } = useTaskRelations(taskId, { enabled: !!taskId });
  const { data: attachmentsData, refetch: refetchAttachments } = useTaskAttachments(taskId, { enabled: !!taskId });
  const addAttachments = useAddTaskAttachments();
  const deleteAttachment = useDeleteTaskAttachment();

  const task = taskData?.data;
  const [newFiles, setNewFiles] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    type: 'task',
    points: '',
    hours: '',
    sprintId: '',
    epicId: '',
    assigneeIds: [],
    dueDate: '',
  });

  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title || '',
        description: task.description || '',
        priority: task.priority?.toLowerCase() || 'medium',
        type: task.type?.toLowerCase() || 'task',
        points: task.points || '',
        hours: task.hours || '',
        sprintId: task.sprintId || '',
        epicId: task.epicId || '',
        assigneeIds: task.assignees?.map((a) => a.userId || a.user?.id) || [],
        dueDate: task.dueDate ? task.dueDate.split('T')[0] : '',
      });
      setHasChanges(false);
    }
  }, [task]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setHasChanges(true);
  };

  const handleDescriptionChange = (content) => {
    setFormData((prev) => ({ ...prev, description: content }));
    setHasChanges(true);
  };

  const handleDeleteExistingAttachment = async (attachmentId) => {
    if (window.confirm('¿Eliminar este archivo adjunto?')) {
      try {
        await deleteAttachment.mutateAsync(attachmentId);
        refetchAttachments();
      } catch (error) {
        console.error('Error deleting attachment:', error);
      }
    }
  };

  const handleSave = async () => {
    await updateTask.mutateAsync({
      id: taskId,
      data: {
        ...formData,
        points: formData.points ? parseInt(formData.points) : null,
        hours: formData.hours ? parseFloat(formData.hours) : null,
        sprintId: formData.sprintId ? parseInt(formData.sprintId) : null,
        epicId: formData.epicId ? parseInt(formData.epicId) : null,
        dueDate: formData.dueDate || null,
      },
    });

    // Upload new attachments
    if (newFiles.length > 0) {
      try {
        await addAttachments.mutateAsync({ taskId, files: newFiles });
        setNewFiles([]);
        refetchAttachments();
      } catch (error) {
        console.error('Error uploading attachments:', error);
      }
    }

    setHasChanges(false);
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      setSelectedTaskId(null);
      setActiveView('kanban');
    }
  };

  const handleDeleteTask = async () => {
    try {
      await deleteTaskMutation.mutateAsync(taskId);
      handleBack();
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const handleSprintClick = () => {
    if (task?.sprint?.id) {
      setSelectedSprintId(task.sprint.id);
      setActiveView('sprint-detail');
    }
  };

  const sprintOptions = sprints.map((s) => ({ value: s.id, label: s.name }));
  const epicOptions = epics.map((e) => ({ value: e.id, label: e.name }));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!task) {
    return (
      <div className="p-8 text-center">
        <p className="text-slate-500">Tarea no encontrada</p>
        <Button variant="secondary" onClick={handleBack} className="mt-4">
          Volver
        </Button>
      </div>
    );
  }

  const priority = priorityColors[formData.priority] || priorityColors.medium;
  const statusConfig = STATUS_CONFIG[task.status] || { label: task.status, color: '#64748b', bg: '#f1f5f9' };

  return (
    <div className="pb-8 max-w-7xl mx-auto">
      {/* Header - más compacto */}
      <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={handleBack} className="!px-2">
            ← Volver
          </Button>
          <div className="flex items-center gap-2 px-3 py-1 bg-slate-100 rounded-lg">
            <span className="text-lg">{typeIcons[formData.type] || '📋'}</span>
            <span className="text-sm text-slate-600 font-mono">#{task.id}</span>
          </div>
          <Badge style={{ backgroundColor: statusConfig.bg, color: statusConfig.color }}>
            {statusConfig.label}
          </Badge>
          <Badge style={{ backgroundColor: priority.bg, color: priority.color }}>
            {priority.label}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">
            Actualizada {new Date(task.updatedAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
          </span>
          {!showDeleteConfirm ? (
            <Button
              variant="ghost"
              onClick={() => setShowDeleteConfirm(true)}
              size="sm"
              style={{ color: '#ef4444' }}
            >
              Eliminar
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-sm text-red-500 font-medium">¿Eliminar tarea?</span>
              <Button variant="ghost" onClick={() => setShowDeleteConfirm(false)} size="sm">
                No
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteTask}
                loading={deleteTaskMutation.isPending}
                size="sm"
              >
                Sí, eliminar
              </Button>
            </div>
          )}
          {hasChanges && (
            <Button variant="primary" onClick={handleSave} loading={updateTask.isPending} size="sm">
              Guardar cambios
            </Button>
          )}
        </div>
      </div>

      {/* Main content - layout mejorado */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Left column - Main info (8 cols) */}
        <div className="lg:col-span-8 space-y-6">
          {/* Title & Description - Card principal */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            {/* Título grande y visible */}
            <input
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Título de la tarea"
              className="w-full text-xl font-semibold text-slate-800 border-none outline-none bg-transparent mb-3 placeholder-slate-400"
              style={{ fontSize: '1.25rem', lineHeight: '1.4' }}
            />

            <div className="mb-4">
              <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Descripción</label>
              <RichTextEditor
                value={formData.description}
                onChange={handleDescriptionChange}
                placeholder="Descripción de la tarea..."
                minHeight={120}
                attachments={attachmentsData?.data || []}
              />
            </div>

            {/* Archivos Adjuntos - más compacto */}
            <div>
              <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Archivos Adjuntos</label>
              <FileAttachment
                files={newFiles}
                existingAttachments={attachmentsData?.data || []}
                onChange={(files) => {
                  setNewFiles(files);
                  setHasChanges(true);
                }}
                onRemoveExisting={handleDeleteExistingAttachment}
                compact
              />
            </div>
          </div>

          {/* Details Grid - más compacto con 3 columnas */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Detalles</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Select
                label="Tipo"
                name="type"
                value={formData.type}
                onChange={handleChange}
                options={typeOptions}
                size="sm"
              />
              <Select
                label="Prioridad"
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                options={priorityOptions}
                size="sm"
              />
              <Select
                label="Story Points"
                name="points"
                value={formData.points}
                onChange={handleChange}
                options={pointsOptions}
                placeholder="Seleccionar..."
                size="sm"
              />
              <Input
                label="Horas est."
                name="hours"
                type="number"
                min="0"
                step="0.5"
                value={formData.hours}
                onChange={handleChange}
                placeholder="0"
                size="sm"
              />
              <Select
                label="Sprint"
                name="sprintId"
                value={formData.sprintId}
                onChange={handleChange}
                options={sprintOptions}
                placeholder="Sin sprint"
                size="sm"
              />
              <Select
                label="Epic"
                name="epicId"
                value={formData.epicId}
                onChange={handleChange}
                options={epicOptions}
                placeholder="Sin epic"
                size="sm"
              />
              <Input
                label="Fecha Límite"
                name="dueDate"
                type="date"
                value={formData.dueDate}
                onChange={handleChange}
                size="sm"
              />
            </div>

            {/* Asignados - sección separada y más clara */}
            <div className="mt-4 pt-4 border-t border-slate-100">
              <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Asignados</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {users.map((user) => {
                  const isSelected = formData.assigneeIds.includes(user.id);
                  return (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => {
                        setFormData((prev) => ({
                          ...prev,
                          assigneeIds: isSelected
                            ? prev.assigneeIds.filter((id) => id !== user.id)
                            : [...prev.assigneeIds, user.id],
                        }));
                        setHasChanges(true);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '6px 12px',
                        borderRadius: '8px',
                        border: isSelected ? '2px solid #6366f1' : '1px solid #e5e7eb',
                        backgroundColor: isSelected ? '#eef2ff' : '#f9fafb',
                        cursor: 'pointer',
                        fontSize: '13px',
                        color: isSelected ? '#4f46e5' : '#374151',
                        fontWeight: isSelected ? '600' : '500',
                        transition: 'all 0.15s',
                      }}
                    >
                      <span
                        style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '11px',
                          fontWeight: '600',
                          flexShrink: 0,
                          background: user.avatar
                            ? `url(${user.avatar}) center/cover`
                            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        }}
                      >
                        {!user.avatar && user.name?.charAt(0).toUpperCase()}
                      </span>
                      <span style={{ whiteSpace: 'nowrap' }}>{user.name || 'Sin nombre'}</span>
                      {isSelected && <span style={{ color: '#6366f1', marginLeft: '4px' }}>✓</span>}
                    </button>
                  );
                })}
                {users.length === 0 && (
                  <span style={{ fontSize: '13px', color: '#9ca3af', padding: '8px 0' }}>No hay miembros en el proyecto</span>
                )}
              </div>
            </div>
          </div>

          {/* Task Relations - más compacto */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <TaskRelationSelector
              taskId={taskId}
              projectId={task.projectId}
              currentRelations={relationsData?.data || { blocking: [], blockedBy: [], relatedTo: [] }}
              onRelationChange={refetchRelations}
              compact
            />
          </div>

          {/* Comments */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <CommentSection taskId={taskId} currentUserId={currentUser?.id} />
          </div>
        </div>

        {/* Right column - Activity & Info (4 cols) */}
        <div className="lg:col-span-4 space-y-6">
          {/* Task Meta - Más prominente arriba */}
          <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200 p-4">
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Información</h4>
            <div className="space-y-2.5 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-slate-500">Creada</span>
                <span className="text-slate-700 font-medium">
                  {new Date(task.createdAt).toLocaleDateString('es-ES', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                </span>
              </div>
              {task.sprint && (
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">Sprint</span>
                  <button
                    onClick={handleSprintClick}
                    className="text-indigo-600 font-medium bg-indigo-50 px-2 py-0.5 rounded hover:bg-indigo-100 transition-colors cursor-pointer"
                    title="Ver detalle del sprint"
                  >
                    🏃 {task.sprint.name}
                  </button>
                </div>
              )}
              {task.epic && (
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">Epic</span>
                  <span className="text-slate-700 font-medium bg-white px-2 py-0.5 rounded">{task.epic.name}</span>
                </div>
              )}
              {task.dueDate && (
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">Vence</span>
                  <span className="text-slate-700 font-medium">
                    {formatDay(task.dueDate, { day: 'numeric', month: 'short' })}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Activity Feed - con scroll limitado */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <ActivityFeed entity="task" entityId={taskId} limit={15} compact />
          </div>
        </div>
      </div>
    </div>
  );
}

TaskDetailPage.propTypes = {
  taskId: PropTypes.number.isRequired,
  sprints: PropTypes.array,
  epics: PropTypes.array,
  users: PropTypes.array,
  onBack: PropTypes.func,
};
